import type {
  CrmConnectionRow,
  HubSpotContactProperty,
  HubSpotMappingSaveBody,
  IntegrationsApi as IntegrationsApiShape,
  SalesforceConnectionSaveBody,
} from '@viq-admin/screens';
import { superadminFetch } from './dataProvider';
import { testConnection } from './crm';

/**
 * The console's closure over `superadminFetch` for the shared Integrations
 * screens (`packages/screens/src/integrations/*`, p6 Task 2) — see that
 * package's "Closure contract" doc comment (`types.ts`). `packages/*` never
 * fetches or names an endpoint directly (closure-only invariant,
 * docs/feature-authoring.md); every network call the shared screens make
 * flows through this object, injected by the resource wrappers in
 * `resources/integrations/*`.
 *
 * Routes verified against the backend registration (source of truth, NOT the
 * old console call sites — see the PUT note below):
 * `viq-backend/apps/superadmin-console/index.ts`:
 *   GET    /v1/superadmin/crm/connections            list    ({ connections: [...] })
 *   GET    /v1/superadmin/crm/connections/{id}       get
 *   POST   /v1/superadmin/crm/connections            create
 *   PUT    /v1/superadmin/crm/connections/{id}       update  (204 is NOT this
 *     route — see delete below; update returns 200 + the record)
 *   DELETE /v1/superadmin/crm/connections/{id}       delete  (204 No Content)
 *   POST   /v1/superadmin/crm/connections/{id}/test  test    ({ status, message })
 *   GET    /v1/superadmin/crm/connections/{id}/hubspot/properties
 *                                                     hubspot properties (Task 7) — ({ properties: [...] })
 *   PUT    /v1/superadmin/crm/connections/{id}/salesforce-profile
 *                                                     staff-only Salesforce settings (spec 2026-09-28 §4.2) — body { profile }
 *
 * `updateHubSpotMapping` (Task 7) is NOT a separate route — it PUTs the same
 * `/v1/superadmin/crm/connections/{id}` as `updateConnection` above, just with
 * a `HubSpotMappingSaveBody` in place of a `SalesforceConnectionSaveBody`; the
 * backend's `CrmConnectionPutInputSchema` is a discriminated union of the two
 * shapes on `crmSource`, and `putCrmConnectionCore` refuses a body whose
 * `crmSource` doesn't match the row's own.
 *
 * `updateConnection` uses PUT, NOT PATCH: the OLD console routed edits
 * through Refine's generic `dataProvider.update`, which always sends PATCH —
 * but the backend registers this route PUT-only
 * (`saCrmConnectionsUpdate: ['PUT']`), so that generic path was silently
 * mismatched. Calling `superadminFetch` directly here (bypassing Refine's
 * dataProvider) fixes the verb rather than reproducing the bug.
 *
 * `updateConnection`'s body also carries `companyId`, even though the console
 * edit form never lets an operator choose or change it (ruling 1 — the
 * company renders read-only OUTSIDE the form). That's a UI rule, not a wire
 * one: the backend's PUT schema (`CrmConnectionPutInputSchema`) requires
 * `companyId` structurally on every PUT, same as POST. The edit wrapper
 * (`resources/integrations/edit.tsx`) attaches the record's own
 * (already-fetched, immutable) `companyId` before calling this — it is never
 * sourced from user input.
 */
interface ConnectionsListResponse {
  connections: CrmConnectionRow[];
}

interface HubSpotPropertiesResponse {
  properties: HubSpotContactProperty[];
}

export const integrationsApi: IntegrationsApiShape = {
  listConnections: async () => {
    const body = await superadminFetch<ConnectionsListResponse>(
      '/v1/superadmin/crm/connections',
    );
    return body.connections ?? [];
  },

  getConnection: (id: string) =>
    superadminFetch<CrmConnectionRow>(`/v1/superadmin/crm/connections/${id}`),

  createConnection: (body: SalesforceConnectionSaveBody) =>
    superadminFetch<CrmConnectionRow>('/v1/superadmin/crm/connections', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  updateConnection: (id: string, body: SalesforceConnectionSaveBody) =>
    superadminFetch<CrmConnectionRow>(`/v1/superadmin/crm/connections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  deleteConnection: (id: string) =>
    superadminFetch<void>(`/v1/superadmin/crm/connections/${id}`, {
      method: 'DELETE',
    }),

  // Reuses the already-tested wrapper in ./crm rather than duplicating the
  // endpoint construction here — same route (POST .../{id}/test), same
  // `{status, message}` wire shape `IntegrationsApi['testConnection']` wants.
  testConnection,

  // Same route + verb as updateConnection above — the backend's PUT schema
  // is a discriminated union on `crmSource`, so a HubSpotMappingSaveBody
  // lands on the config-only hubspot arm instead of the salesforce one.
  updateHubSpotMapping: (id: string, body: HubSpotMappingSaveBody) =>
    superadminFetch<CrmConnectionRow>(`/v1/superadmin/crm/connections/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  listHubSpotProperties: async (id: string) => {
    const body = await superadminFetch<HubSpotPropertiesResponse>(
      `/v1/superadmin/crm/connections/${id}/hubspot/properties`,
    );
    return body.properties ?? [];
  },

  // Staff-only Salesforce settings (spec 2026-09-28 §4.2): body `{ profile }`,
  // `null` removes the block. A 422 carries per-path issues in `errors`.
  updateSalesforceProfile: (id: string, profile: Record<string, unknown> | null) =>
    superadminFetch<CrmConnectionRow>(`/v1/superadmin/crm/connections/${id}/salesforce-profile`, {
      method: 'PUT',
      body: JSON.stringify({ profile }),
    }),
};
